/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useRef, useState } from 'react'
import { useRequest } from '../../hooks/UseRequest'
import { useResendCooldown } from '../../hooks/UseResendCooldown'
import { useSelector, useDispatch } from 'react-redux'
import Loading from '../../components/Loading'
import { Link } from 'react-router-dom'
import NoRecord from '../../components/elements/NoRecord'
import { selectSubpanelRerender, triggerRerender } from '../../redux/rerenderSlice'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRefresh, faPaperPlane, faEye, faCopy, faCircleInfo } from '@fortawesome/free-solid-svg-icons'
import { selectUser } from '../../redux/userSlice'
import { hasUserPermission } from '../../utils/permissionHelper'
import { setNotification } from '../../redux/notificationSlice'
import { DateFormat, FormatISODateTime } from '../../helpers/formatting'

export function BankChangeDashletContent(props) {
  const { dropdownOptions, row, setRow } = props;
  const active = useSelector(selectUser);
  const activePermissions = active.spoofed_user.user_permission_options ? active.spoofed_user.user_permission_options : active.data.user_permission_options
  const requests = useRequest()
  const [loaded, setLoaded] = useState(false)
  const [sendingBankLogin, setSendingBankLogin] = useState(false)
  const [bankLoginRequests, setBankLoginRequests] = useState([])
  const [refreshingRequestId, setRefreshingRequestId] = useState(null)
  const [resendingRequestId, setResendingRequestId] = useState(null)
  const [refreshingAll, setRefreshingAll] = useState(false)
  const [pollingInterval, setPollingInterval] = useState(null)
  const sendingBankLoginRef = useRef(false)
  const resendingRef = useRef(false)
  const { startCooldown, isCoolingDown } = useResendCooldown()
  const dispatch = useDispatch()

  // Processing status dropdown options for bank login requests
  const processingStatusOptions = Object.values(dropdownOptions).filter((option) =>
    option.code_key &&
    (option.code_key.startsWith('processing_status') || option.code_key.startsWith('bank_transaction_provider_call_outcome')))

  // Helper function to get status label from outcome_status_dropdown_option_id
  const getStatusLabel = (statusId) => {
    const statusOption = processingStatusOptions.find((option) => option.id === statusId)
    if (!statusOption) return 'Unknown'

    switch (statusOption.code_key) {
      case 'processing_status_pending':
        return 'Not Started'
      case 'processing_status_processing':
        return 'Started'
      case 'processing_status_completed':
        return 'Completed'
      case 'bank_transaction_provider_call_outcome_decisionlogic_unsupported_bank':
        return 'Unsupported Bank'
      case 'bank_transaction_provider_call_outcome_decisionlogic_approved':
        return 'Approved'
      case 'bank_transaction_provider_call_outcome_decisionlogic_declined':
        return 'Declined'
      default:
        return statusOption.name || 'Unknown'
    }
  }

  // Resolve the provider dropdown option (Chirp vs DecisionLogic) for a bank login request
  const getProviderOption = (request) => {
    if (request.provider_ddo) return request.provider_ddo
    if (request.provider_dropdown_option_id && dropdownOptions) {
      return Object.values(dropdownOptions).find((opt) => opt?.id === request.provider_dropdown_option_id) || null
    }
    return null
  }

  let messageTitle = '';
  let messageType = '';
  let message = row?.message || row?.data?.bank_change_outcome_ddo?.optional_value_1 || '';
  if (row?.success) {
    // Successful bank change
    messageType = 'is-success';
    messageTitle = 'Bank Change Successful';
    // Special case for bank_account_action None
    if (row?.bank_account_action === 'None' &&
      row?.data?.bank_change_outcome_ddo?.code_key === 'bank_change_outcome_none') {
      messageType = 'is-warning';
    }
  } else {
    messageTitle = 'Bank Change Unsuccessful';
    messageType = 'is-danger';
    const outcomeCode = row?.data?.bank_change_outcome_ddo?.code_key;
    const processingStatus = row?.data?.model_processing_status_ddo?.code_key;
    const validationResult = row?.validation_result;

    if (outcomeCode === 'bank_change_outcome_duplicate_pending') {
      messageTitle = 'Duplicate Bank Account Pending';
      messageType = 'is-warning';

      const customerInfo = row?.data?.duplicate_bank_account?.customer
        ? 'customer ' + '<strong><a href=/customer/' + row?.data?.duplicate_bank_account?.customer_id + '>' + row?.data?.duplicate_bank_account?.customer?.label + '</strong></a>'
        : 'Another customer';

      message = 'This account number is currently associated with ' + customerInfo + '. Please reach out to the assistance channel for further assistance.';
    } else if (processingStatus === 'processing_status_verify') {
      const mgrApprovalCode = row?.data?.new_bank_account?.mgr_approval_status_ddo?.code_key;

      if (mgrApprovalCode === 'mgr_approval_status_approved') {
        messageTitle = 'Manager Approval Granted';
        messageType = 'is-success';
      } else if (mgrApprovalCode === 'mgr_approval_status_required') {
        messageTitle = 'Manager Approval Required';
        messageType = 'is-warning';
      } else if (mgrApprovalCode === 'mgr_approval_status_declined') {
        messageTitle = 'Manager Approval Declined';
      } else {
        messageTitle = 'Manager Approval Status Unknown';
      }
    } else if (validationResult === 'Denied') {
      messageTitle = 'Bank Change Denied';
    } else if (['Error', 'None'].includes(validationResult)) {
      messageTitle = 'Bank Change Error';
    }
  }

  const processDuplicate = async (action) => {
    setLoaded(true)
    if (!row?.data?.id) {
      // Fallback in case row.data.id is not available
      dispatch(setNotification({ type: 'error', body: 'Unable to process duplicate bank account, missing ID.' }))
      return;
    }
    requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/bank_change/${row?.data?.id}/${action}`)
    try {
      const response = await requests.modify()
      if (response.status === 200) {
        dispatch(triggerRerender({ subpanelId: 'manual_verification' }));
        setRow(response?.data)
      }
      setLoaded(false)
    } catch (error) {
      setLoaded(false)
      dispatch(setNotification({ type: 'error', body: error.response?.data?.message }))
    }
  }

  const sendNewBankLogin = async () => {
    if (!row?.data?.id || sendingBankLoginRef.current || isCoolingDown(row?.data?.customer_id)) return;
    sendingBankLoginRef.current = true
    setSendingBankLogin(true)
    try {
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/bank_change/${row?.data?.id}/bank_transaction/decision_logic_login_request`)
      const response = await requests.post({})
      if (response?.status === 201 && response?.data?.success) {
        const data = response?.data?.data
        if (data) {
          setBankLoginRequests((prev) => [...prev, data])
          startCooldown(row?.data?.customer_id)
        }
        // Only show message if success is false
        if (response?.data?.message && !response?.success) {
          dispatch(setNotification({ type: 'error', body: response.data.message }))
        }
      } else {
        const message = response?.data?.message || 'Failed to send bank login request.'
        dispatch(setNotification({ type: 'error', body: message }))
      }
    } catch (error) {
      dispatch(setNotification({ type: 'error', body: error?.response?.data?.message || 'Failed to send bank login request.' }))
    } finally {
      sendingBankLoginRef.current = false
      setSendingBankLogin(false)
    }
  }

  const fetchBankLoginRequests = async () => {
    if (!row?.data?.id) return;
    try {
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/bank_change/${row?.data?.id}/bank_transaction/decision_logic_request_status?refresh=true`)
      const response = await requests.get()
      if (response?.data?.data && Array.isArray(response.data.data)) {
        setBankLoginRequests(response.data.data)
        // Only show message if success is false
        if (response?.data?.message && !response?.data?.success) {
          dispatch(setNotification({ type: 'error', body: response.data.message }))
        }
      } else {
        const message = response?.data?.message || 'Failed to fetch bank loginsss requests.'
        dispatch(setNotification({ type: 'error', body: message }))
      }
    } catch (error) {
      dispatch(setNotification({ type: 'error', body: error?.response?.data?.message || 'Failed to fetch bank login requests.' }))
    }
  }

  const refreshDecisionLogicStatuses = async () => {
    setRefreshingAll(true)
    // Always fetch fresh data when refresh button is clicked
    await fetchBankLoginRequests()
    setRefreshingAll(false)
  }

  // Check if any bank login requests need polling
  const needsPolling = () => {
    return bankLoginRequests.some((req) => {
      let statusOption = null;

      // Handle both formats: direct ID or object with processing_status_ddo
      if (typeof req === 'object' && req.processing_status_ddo) {
        // Format from initial load: { processing_status_ddo: { id, code_key, name } }
        statusOption = req.processing_status_ddo;
      } else if (req.processing_status_dropdown_option_id) {
        // Format from refreshed data: processing_status_dropdown_option_id
        statusOption = processingStatusOptions.find((option) => option.id === req.processing_status_dropdown_option_id);
      }

      return statusOption?.code_key === 'processing_status_processing' || statusOption?.code_key === 'processing_status_pending';
    });
  }

  // Start polling if needed
  const startPollingIfNeeded = () => {
    const hasPermission = hasUserPermission(activePermissions, 'bank_transaction_provider_call_create');
    if (row?.decision_logic_required && needsPolling() && !pollingInterval && hasPermission) {
      const interval = setInterval(() => {
        fetchBankLoginRequests();
      }, 30000); // 30 seconds
      setPollingInterval(interval);
    } else if ((!row?.decision_logic_required || !needsPolling() || !hasPermission) && pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  }

  const resendDecisionLogicRequest = async (requestId) => {
    if (!requestId || resendingRef.current || isCoolingDown(row?.data?.customer_id)) return;
    resendingRef.current = true
    setResendingRequestId(requestId)
    try {
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/bank_transaction_provider_call/${requestId}/decision_logic_resend_email`)
      const response = await requests.post({})
      if (response?.data?.success) {
        startCooldown(row?.data?.customer_id)
      } else {
        const message = response?.data?.message || 'Failed to resend bank login request.'
        dispatch(setNotification({ type: 'error', body: message }))
      }
    } catch (error) {
      dispatch(setNotification({ type: 'error', body: error?.response?.data?.message || 'Failed to resend bank login request.' }))
    } finally {
      resendingRef.current = false
      setResendingRequestId(null)
    }
  }

  const copyToClipboard = async (link) => {
    try {
      await navigator.clipboard.writeText(link)
      dispatch(setNotification({ type: 'success', body: 'Link copied to clipboard!' }))
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = link
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      dispatch(setNotification({ type: 'success', body: 'Link copied to clipboard!' }))
    }
  }

  // Set bank login requests from row.data if available
  useEffect(() => {
    if (row?.data?.decision_logic_provider_calls && Array.isArray(row.data.decision_logic_provider_calls)) {
      setBankLoginRequests(row.data.decision_logic_provider_calls)
    }
  }, [row?.data?.decision_logic_provider_calls])

  // Handle polling based on bank login request statuses
  useEffect(() => {
    startPollingIfNeeded();

    // Cleanup interval on unmount
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
    };
  }, [bankLoginRequests, pollingInterval, row?.decision_logic_required, activePermissions])

  return (
    <>
      <div
        className={`columns is-full mt-2 ml-1 mb-2 mr-2 notification ${messageType} is-light p-0 is-align-items-center`}
        data-testid="bank-change-dashlet-notification"
      >
        <div className="column">
          <h3 className="has-text-weight-bold" data-testid="bank-change-dashlet-title">{messageTitle}</h3>
          <span dangerouslySetInnerHTML={{ __html: message }} />
        </div>
      </div>
      <div className="lox-dashlet-detail__item py-0 px-1">
      </div>
      {row?.data?.bank_change_outcome_ddo?.code_key === 'bank_change_outcome_duplicate_pending' && hasUserPermission(activePermissions, 'confirm_duplicate_bank_account_update') && (
      <div className={`lox-form__buttons-wrapper has-text-centered`}>
          <button
            type="button"
            className="button has-background-grey has-text-white pl-3 pr-3 m-2 mr-2"
            onClick={() => processDuplicate('decline')}
            disabled={loaded}
          >Reject</button>
          <button
            type="button"
            className="button is-link has-text-white pl-3 pr-3 m-2"
            onClick={() => processDuplicate('approve')}
            disabled={loaded}
          >Confirm</button>
        </div>
      )}
      <div className="lox-dashlet-detail__item py-0 px-1">
        <p className="lox-dashlet-detail__item__label has-text-grey is-size-8">
          New Account Number
        </p>
        <p className="lox-dashlet-detail__item__data has-text-link is-size-8">
          <Link
            key={`bank-change-dashlet-view-${row?.data?.new_bank_account.id}`}
            to={`/bank_account/${row?.data?.new_bank_account.id}`}
            className="lox-has-text-decoration-none"
          >
            {(row?.data?.new_bank_account?.bank_routing_number?.bank?.name || '#'.repeat(6)) + (row?.data?.new_bank_account?.bank_routing_number?.bank?.name ? ' ' : '') + (row?.data?.new_bank_account?.bank_account_number_last_four || 'XXXX')}
          </Link>
        </p>
      </div>
      <div className="divider m-0 mx-1"></div>
      <div className="lox-dashlet-detail__item py-0 px-1">
        <p className="lox-dashlet-detail__item__label has-text-grey is-size-8">
          Old Account Number
        </p>
        <p className={`lox-dashlet-detail__item__data ${row?.data?.old_bank_account?.id && 'has-text-link'} is-size-8`}>
          {row?.data?.old_bank_account?.id ? (
            <Link
              key={`bank-change-dashlet-view-${row?.data?.old_bank_account?.id}`}
              to={`/bank_account/${row?.data?.old_bank_account?.id}`}
              className="lox-has-text-decoration-none"
            >
              {(row?.data?.old_bank_account?.bank_routing_number?.bank?.name || '#'.repeat(6)) + (row?.data?.old_bank_account?.bank_routing_number?.bank?.name ? ' ' : '') + (row?.data?.old_bank_account?.bank_account_number_last_four || 'XXXX')}
            </Link>
          ) : '-'}
        </p>
      </div>
      <div className="divider m-0 mx-1"></div>
      {row?.data?.duplicate_bank_account_id && (
      <div>
        <div className="lox-dashlet-detail__item py-0 px-1">
          <p className="lox-dashlet-detail__item__label has-text-grey is-size-8">
            Duplicate Account
          </p>
          <p className={`lox-dashlet-detail__item__data ${row?.data?.duplicate_bank_account_id && 'has-text-link'} is-size-8`}>
              <Link
                key={`bank-change-dashlet-view-${row?.data?.duplicate_bank_account_id}`}
                to={`/bank_account/${row?.data?.duplicate_bank_account_id}`}
                className="lox-has-text-decoration-none"
              >
                {row?.data?.duplicate_bank_account?.customer?.label} - {(row?.data?.duplicate_bank_account?.bank_routing_number?.bank?.name || '#'.repeat(6)) + (row?.data?.duplicate_bank_account?.bank_routing_number?.bank?.name ? ' ' : '') + (row?.data?.duplicate_bank_account?.bank_account_number_last_four || 'XXXX')}
              </Link>
          </p>
        </div>
        <div className="divider m-0 mx-1"></div>
      </div>
      )}
      <div className="lox-dashlet-detail__item py-0 px-1">
        <p className="lox-dashlet-detail__item__label has-text-grey is-size-8">
          Source
        </p>
        <p className="lox-dashlet-detail__item__data has-text-dark is-size-8">
          {dropdownOptions[row?.data?.action_source_dropdown_option_id]?.name || '-'}
        </p>
      </div>
      <div className="divider m-0 mx-1"></div>
      <div className="lox-dashlet-detail__item py-0 px-1">
        <p className="lox-dashlet-detail__item__label has-text-grey is-size-8">
          Requested By
        </p>
        <p className={`lox-dashlet-detail__item__data ${row?.data?.created_by && 'has-text-link'} is-size-8`}>
          {row?.data?.created_by ? (
            <Link
              key={`bank-change-dashlet-view-${row?.data?.created_by}`}
              to={`/user/${row?.data?.created_by}`}
              className="lox-has-text-decoration-none"
            >
              {row?.data?.created_by_user?.label}
            </Link>
          ) : '-'}
        </p>
      </div>
      {row?.validation_result !== 'None' && (
        <>
          <div className="divider m-0 mx-1"></div>
          <div className="lox-dashlet-detail__item py-0 px-1">
            <p className="lox-dashlet-detail__item__label has-text-grey is-size-8">
              Validation Status
            </p>
            <p className="lox-dashlet-detail__item__data has-text-dark is-size-8">
              {row?.validation_result}
            </p>
          </div>
        </>
      )}
      <div className="divider m-0 mx-1"></div>
      <div className="lox-dashlet-detail__item py-0 px-1">
        <p className="lox-dashlet-detail__item__label has-text-grey is-size-8">
          Bank Change Outcome
        </p>
        <p className="lox-dashlet-detail__item__data has-text-dark is-size-8">
          {dropdownOptions[row?.data?.bank_change_outcome_dropdown_option_id]?.name || '-'}
        </p>
      </div>
      <div className="divider m-0 mx-1"></div>
      <div className="lox-dashlet-detail__item py-0 px-1">
        <p className="lox-dashlet-detail__item__label has-text-grey is-size-8">
          Action Reason
        </p>
        <p className="lox-dashlet-detail__item__data has-text-dark is-size-8">
          {row?.data?.action_reason || '-'}
        </p>
      </div>
      {row?.decision_logic_required && hasUserPermission(activePermissions, 'bank_transaction_provider_call_create') && (
        <>
          <div className="divider m-0 mx-1"></div>
          <div className="lox-dashlet-detail__item py-0 px-1">
            <p className="lox-dashlet-detail__item__label has-text-grey is-size-8 is-flex is-align-items-center">
              <span>Bank Logins</span>
              <button
                type="button"
                className="button is-white is-small m-0 ml-2"
                onClick={refreshDecisionLogicStatuses}
                disabled={refreshingAll}
              >
                <span className="icon">
                  <FontAwesomeIcon icon={faRefresh} className={`${refreshingAll ? 'fa-pulse' : ''}`} />
                </span>
              </button>
            </p>
            {bankLoginRequests && bankLoginRequests.length > 0 ? (
              bankLoginRequests.map((req) => {
                const status = getStatusLabel(req.outcome_status_dropdown_option_id)
                const lastErrorMessage = req.last_error_message
                const providerOption = getProviderOption(req)
                const resultsPath = providerOption?.code_key === 'provider_chirp' ? `/chirp_results/${req.id}` : `/decision_logic_results/${req.id}`
                return (
                  <p key={req.id || req.ext_request_code} className="lox-dashlet-detail__item__data has-text-dark is-size-8 is-flex is-align-items-center is-justify-content-space-between">
                    <span>
                      {req.ext_request_code} - {status}
                      {lastErrorMessage && (
                        <span
                          className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal has-tooltip-fixed-dropdown-menu mr-1 is-flex-shrink-0`}
                          data-tooltip={lastErrorMessage}
                        >
                          <FontAwesomeIcon
                            icon={faCircleInfo}
                            className={`pl-1 has-text-link`}
                          />
                        </span>
                      )}
                      {' - '}
                      {FormatISODateTime(req.created_at)}
                    </span>
                    <div>
                    {status === 'Unsupported Bank' ? (
                      <span>-</span>
                    ) : (
                      (() => {
                        if (resendingRequestId === req.id) {
                          return (
                            <div className="m-0 is-flex is-align-items-center is-justify-content-center">
                              <Loading size="is-size-7" />
                            </div>
                          );
                        }

                        if (status === 'Completed') {
                          const decisionLogicLink = `${window.location.origin}${resultsPath}`;
                          return (
                            <div className="is-flex">
                              <a
                                href={resultsPath}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="button is-white is-small m-0 mr-1"
                              >
                                <span className="icon">
                                  <FontAwesomeIcon icon={faEye} />
                                </span>
                              </a>
                              <button
                                type="button"
                                className="button is-white is-small m-0"
                                onClick={() => copyToClipboard(decisionLogicLink)}
                                title="Copy link to clipboard"
                              >
                                <span className="icon">
                                  <FontAwesomeIcon icon={faCopy} />
                                </span>
                              </button>
                            </div>
                          );
                        }

                        return (
                          <button
                            type="button"
                            className="button is-white is-small m-0"
                            onClick={() => resendDecisionLogicRequest(req.id)}
                            disabled={resendingRequestId !== null || isCoolingDown(row?.data?.customer_id)}
                            title={isCoolingDown(row?.data?.customer_id) ? 'Email sent recently' : 'Resend email'}
                          >
                            <span className="icon">
                              <FontAwesomeIcon icon={faPaperPlane} />
                            </span>
                          </button>
                        );
                      })()
                    )}
                  </div>
                  </p>
                )
              })
            ) : (
              <p className="lox-dashlet-detail__item__data has-text-dark is-size-8">
                -
              </p>
            )}
          </div>
          <div className="divider m-0 mx-1"></div>
          <div className={`lox-form__buttons-wrapper`}>
            <button
              type="button"
              className="button is-link is-small is-fullwidth"
              onClick={sendNewBankLogin}
              disabled={sendingBankLogin || isCoolingDown(row?.data?.customer_id)}
              title={isCoolingDown(row?.data?.customer_id) ? 'Email sent recently' : undefined}
            >
              Send New Bank Login
            </button>
          </div>
        </>
      )}
    </>
  )
}

export default function BankChangeDashlet({ endpointPath, dropdownOptions, onBankChange = null, currentStep = '' }) {
  const requests = useRequest()
  const requesting = useRef(false)
  const [row, setRow] = useState([])
  const [loaded, setLoaded] = useState(false)
  const rerenderValue = useSelector((state) => selectSubpanelRerender(state, 'bank_change_dashlet'))

  const getRow = async () => {
    setLoaded(false)
    requests.setEndpoint(endpointPath)
    try {
      const response = await requests.get()
      setRow(response.data)
      // Only trigger onBankChange if we're on the questions step
      if (
        currentStep === 'questions' &&
        response.data?.success &&
        response.data?.data?.new_bank_account?.is_primary_account &&
        typeof onBankChange === 'function'
      ) {
        const newBankAccount = response.data?.data?.new_bank_account;
        const { bank_routing_number } = newBankAccount;
        // set new bank account into on customer data.
        onBankChange({
          bank_account_number: newBankAccount?.bank_account_number_last_four,
          bank_routing_number: bank_routing_number?.number,
          bank_name: bank_routing_number?.id,
          bank_name_label: bank_routing_number?.bank?.name,
        });
      }
      requesting.current = false
      setLoaded(true)
    } catch (error) {
      setLoaded(true)
      requesting.current = false
    }
  }

  useEffect(() => {
    if (!requesting.current && endpointPath && (!row || !row.data || Object.keys(row?.data).length === 0 || rerenderValue)) {
      requesting.current = true
      getRow()
    }
  }, [endpointPath, rerenderValue])

  return (
    <div className="message is-white lox-dashlet-detail">
      <div className="message-header has-background-primary has-text-white lox-dashlet-detail__title py-0">
        <p className="is-size-4">Last Bank Change Audit</p>
        <p className="is-size-4">
          <span
            className="is-clickable"
            onClick={() => getRow()}
          >
            <FontAwesomeIcon icon={faRefresh} className={`${!loaded ? 'fa-pulse' : ''}`} />
          </span>
        </p>
      </div>
      <div className="message-body py-0 px-1" style={{ lineHeight: 1.40 }}>
        {loaded && row && row.data && Object.keys(row?.data).length > 0 && (
          <BankChangeDashletContent dropdownOptions={dropdownOptions} row={row} setRow={setRow} />
        )}
        {!loaded && <Loading />}
        {loaded && row && row.data && Object.keys(row?.data).length === 0 && <NoRecord />}
      </div>
    </div>
  )
}
